// # packages
import * as React from "react";
import * as ReactDOM from "react-dom";
// # components
import { ErrorBoundary } from "@/components/feedback";

/**
 * Container component that renders props.childProps into props.childComponent
 * This lets us render a component via ReactDOM.render(), wrapped with this component, and update its props.
 *
 * @prop {React.ComponentType<any>} childComponent
 * @prop {Record<string, any>} childProps
 */
class Container extends React.Component<{
  expectedPropKeys: string[];
  childProps: Record<string, any>;
  childComponent: React.ComponentType<any>;
}> {
  static displayName = "Angular->React";

  constructor(props) {
    super(props);

    // bind methods
    this.propsAreSet = this.propsAreSet.bind(this);

    // copy props into state
    this.state = { ...this.props.childProps };
  }

  /**
   * Allows us to set the props of the child component via a reference to this component.
   *
   * @param {this.props} props
   * @returns {void}
   */
  public setChildProps(props): void {
    this.setState(props);
  }

  public render() {
    if (this.props.expectedPropKeys.length && !this.propsAreSet()) {
      return null;
    }

    return (
      <ErrorBoundary>
        <this.props.childComponent {...this.state} />
      </ErrorBoundary>
    );
  }

  /**
   * Determines whether at least one prop has a truthy value.
   *
   * @returns {boolean}
   */
  protected propsAreSet(): boolean {
    return Object.values(this.state).filter((value) => !!value).length > 0;
  }
}

/**
 * Generates the config for an angular component that renders a react component.
 *
 * Usage:
 *  angular.module('app')
 *   .component('user', angularComponent(User, ['forename', 'surname'])
 *
 * @param {React.ComponentType<any>} component
 * @param {Array<string>=[]} propKeys
 * @returns {Record<string, any>}
 */
export function angularComponent(
  reactComponent: React.ComponentType<any>,
  propKeys: Array<string> = [],
): Record<string, any> {
  const config = {
    bindings: propKeys.reduce(
      (bindings, key) => ({ ...bindings, [key]: "<" }),
      {},
    ),
    controller: [
      "$element",
      function ($element) {
        /**
         * Gets an object of props and their values
         *
         * @returns {Record<string, any>}
         */
        const getProps = () =>
          propKeys.reduce((props, key) => ({ ...props, [key]: this[key] }), {});

        /**
         * Wrap our react component in a container so we can update its props manually.
         *
         * @param {React.ComponentType<any>} child
         * @param {Record<string, any>} props
         * @returns {React.ReactElement}
         */
        const makeContainer = (
          child: React.ComponentType<any>,
          props: Record<string, any>,
        ): React.ReactElement => (
          <Container
            expectedPropKeys={propKeys}
            childProps={props}
            childComponent={child}
          />
        );

        // Render the react component into the angular component element
        // =
        // Note: overriding the return type on render() method as it might return void, which we can't check for truthiness.
        // See https://github.com/microsoft/TypeScript/issues/26262
        const instance = ReactDOM.render(
          makeContainer(reactComponent, getProps()),
          $element[0],
        ) as any as React.Component;

        // Update react component when bindings change
        this.$onChanges = () => {
          if (!instance) {
            return;
          }
          instance.setState(getProps());
        };
      },
    ],
    template: "<div ng-transclude></div>",
    transclude: true,
  };

  return config;
}

/**
 * Gets an angular service.
 * Note: these should not be used directly!
 * Make sure to wrap so we can easily replace the implementation as we migrate away from angular.
 *
 * @param {string} serviceName
 * @returns {any}
 */
export function resolveAngularService(serviceName): any {
  const serviceInstance = angular.injector(["app"]).get(serviceName);

  if (!serviceInstance) {
    throw new Error(`Angular service "${serviceName}" not found.`);
  }

  return serviceInstance;
}
